/** The words of the itinerary record's Usage tab (plan §5 "Used by", §9.5, §10.4): the used-by's
 *  groups — every tour that depends on it, the other itineraries showing its days, travellers'
 *  trips added from it — and what each row says. Everything listed here refuses the delete
 *  (the guard's `dependents`), so every row says "blocks Delete", as the activity record's
 *  tour rows do. Pure. */
import type { ItineraryTourRelation, ItineraryUsedByRef, ItineraryUsedByType } from '@/api/itineraries';
import type { UsageGroupDef } from '@/features/catalog/usage/usageModel';
import { consoleRouteFor } from '@/utils/gridLinks';
import { formatZoned } from '@/utils/zonedTime';

/** The Usage tab's groups, in the endpoint's order. */
export const ITINERARY_USAGE_GROUPS: readonly UsageGroupDef<ItineraryUsedByType>[] = [
  { key: 'demo_group_tour', label: 'Tours', noun: 'tours' },
  { key: 'demo_itinerary_group', label: 'Other itineraries', noun: 'other itineraries sharing its days' },
  {
    key: 'demo_user_itinerary',
    label: 'Trips added',
    noun: 'trips added from it',
    unlisted: 'Travellers’ trips added from it · not listed above 1,000 · blocks Delete',
  },
];

/** What nothing using it means: the delete is not refused for its use (a placeholder still is —
 *  its own reason, in Actions). */
export const ITINERARY_USAGE_EMPTY = 'Nothing uses it — no tour, other itinerary or trip';

/** How a tour depends on it, in the refused delete's words (`DEPENDENT_WORDS`). */
const RELATION_WORDS: Readonly<Record<ItineraryTourRelation, string>> = {
  tour_itineraries: 'built on it',
  template_tours: 'uses it as a template',
  crosssell_tours: 'cross-sells it',
};

export const BLOCKS_DELETE = 'blocks Delete';

/** A day in words, the year only when it is not this one: "14 Nov", "3 Oct 2025". */
function day(iso: string, now: Date): string {
  return formatZoned(iso, 'UTC', { weekday: false, time: false, zone: false, now });
}

/** The row's facts after its name: how a tour depends on it (and that it was deleted), that
 *  another itinerary shares its days, when a trip was added and when it travels — then
 *  "blocks Delete". */
export function itineraryUsageFacts(
  group: ItineraryUsedByType,
  row: ItineraryUsedByRef,
  now: Date = new Date(),
): string[] {
  switch (group) {
    case 'demo_group_tour':
      return [
        ...(row.relations ?? []).map((r) => RELATION_WORDS[r]),
        ...(row.deleted ? ['deleted tour'] : []),
        BLOCKS_DELETE,
      ];
    case 'demo_itinerary_group':
      return ['shares its days', BLOCKS_DELETE];
    case 'demo_user_itinerary':
      return [
        ...(row.created_ts ? [`added ${day(row.created_ts, now)}`] : []),
        // A date-only value: noon UTC keeps its day in every zone.
        ...(row.travel_date ? [`travels ${day(`${row.travel_date}T12:00:00Z`, now)}`] : []),
        BLOCKS_DELETE,
      ];
    default:
      return [];
  }
}

/** The row's console page: a tour, an itinerary; a trip has none (its traveller does). */
export function itineraryUsageHref(row: Pick<ItineraryUsedByRef, 'type_key' | 'id'>): string | null {
  return row.type_key === 'demo_user_itinerary' ? null : consoleRouteFor(row.type_key, row.id);
}
